import React, { useContext, useEffect } from "react";
import { useState } from "react";
import { Button, Form, Table } from "react-bootstrap";
import { CustomField } from "../../../types";
import { DefaultCustomField } from "../../../data";
import PortalService from '../../../services/portal.service';
import StateContext from "../../../context/State";
import { useAppSelector } from "../../../../../redux/hooks";
import { NewCustomFieldSetModal } from "../../../../Settings/ClientConnectFirmSettings/CustomFieldSets/newCustomFieldSet";
import { ClientConnectCustomFieldSet } from "../../../../Settings/ClientConnectFirmSettings/CustomFieldSets/types";
import { lddGlobalSessionInfo } from "../../../../navigationTypes";
import { getCustomFieldSet } from "../../../../Settings/ClientConnectFirmSettings/CustomFieldSets/customFieldSetFuncs";
import { RPWDataReader, getDataReader } from "../../../../../livedataFuncs";
import { lddStateObject } from "../../../../../redux/reducer";
import { distinctIntakeFormNamesFromPortalRows } from "../../Dashboard/CustomFields/intakeTemplateMerge";


export type AddCustomFieldsProps = {
    setCustomFields: (arg0: CustomField[]) => void;
    setCustomFieldSetNames: (arg0: string[]) => void;
    customFieldSetNames: string[];
    addRow?: boolean;
    setAddRow?: (arg0: boolean) => void;
    afterInit: boolean;
    modalVisible?: boolean;
}

export type CheckedSetArray = {
    name: string;
    checked: boolean;
}

const AddCustomFields = (props: AddCustomFieldsProps) => {

    const { state } = useContext(StateContext);
    const portalService = new PortalService(state);
    const defaultCustomField: CustomField = { ...DefaultCustomField };
    const [fields, setFields] = useState<CustomField[]>([defaultCustomField]);
    const sessionInfo: lddGlobalSessionInfo = useAppSelector((state) => state.SessionInfo);
    const [show, setShow] = useState<boolean>(false)
    const [customFieldSets, setCustomFieldSets] = useState<ClientConnectCustomFieldSet[]>([])
    const [editFieldSet, setEditFieldSet] = useState<ClientConnectCustomFieldSet>(null);
    const [selectedCustomFieldSets, setSelectedCustomFieldSets] = useState<string[]>([])
    const livedataRootContainer = useAppSelector((state: lddStateObject) => state.currentNavigation.livedataRootContainer);
    const DataReaderObj: RPWDataReader = getDataReader(livedataRootContainer.dataRoot);
    const [clientInfo, setClientInfo] = useState([]);

    useEffect(() => { getSelectedCustomFieldSets(); }, [])
    useEffect(() => { getCustomFields(); }, [])
    useEffect(() => {
        if (!props.afterInit || props.modalVisible !== true || !state.portalId) {
            return;
        }
        void getCustomFields();
    }, [props.afterInit, props.modalVisible, state.portalId])
    useEffect(() => { getClientInfo(); }, [])
    useEffect(() => {
        const updateCustomFieldSet = async () => {
            const response = await getCustomFieldSet(sessionInfo.sid);
            if (response && response.length !== 0) {
                const processedFieldSets = response.map(fieldSet => {
                    const processedFields = fieldSet.fields.map((field) => {
                        if (field.perClient) {
                            let identifierObj = field.identifier ? JSON.parse(field.identifier) : {};
                            clientInfo.forEach((clientName) => {
                                if (!identifierObj.hasOwnProperty(clientName)) {
                                    identifierObj[clientName] = "";
                                }
                            });
                            return {
                                ...field,
                                identifier: JSON.stringify(identifierObj)
                            };
                        } else {
                            return {...field};
                        }
                    });
                    return {
                        ...fieldSet,
                        fields: processedFields
                    };
                });
                setCustomFieldSets(processedFieldSets);
            }
        };
        updateCustomFieldSet();
    }, [fields, clientInfo]);
    
    useEffect(() => {
        const processedFields = fields.map((field) => {
            if (field.perClient) {
                const clientValues = clientInfo.reduce((obj, clientName) => {
                    obj[clientName] = ""; // Start with empty strings for each client
                    return obj;
                }, {});
    
                // Modify only the identifier to hold the JSON string
                return {
                    ...field,
                    identifier: field.identifier ?? JSON.stringify(clientValues)
                };
            } else {
                return {...field};
            }
        });
    
        props.setCustomFields(processedFields.filter(f => f.name)); 
    
    }, [fields]);

    const getClientInfo = async () => {
        const province = await portalService.getProvince();
        const names = DataReaderObj.getUserNames(province);
        setClientInfo(names);
    }

    const handleClose = () => {
        setEditFieldSet(null)
        setShow(false);
    }

    const handleShow = async () => {
        setShow(true);
    };

    const addNewRow = () => {
        const emptyCustomField: CustomField = { ...DefaultCustomField };
        const prevFields = [...fields];
        prevFields.push(emptyCustomField);
        setFields(prevFields)
    }

    const handleFieldCleared = (index: number) => {
        const prevFields = [...fields];
        prevFields.splice(index, 1)
        setFields(prevFields)
    }

    const handleChange = (event, index: number) => {
        const prevFields = [...fields];
        prevFields[index].name = event.target.value;
        setFields(prevFields)
    }

    const handleSwitchToggle = (index: number, event) => {
        const prevFields = [...fields];
        prevFields[index].perClient = event.target.checked;
        setFields(prevFields);
    }

    const handleFieldSetSelected = (index, e) => {
        if (e.target.checked) {
            props.setCustomFieldSetNames([...props.customFieldSetNames, customFieldSets[index].fieldSetName])
            setSelectedCustomFieldSets([...selectedCustomFieldSets, customFieldSets[index].fieldSetName])
        } else {
            for (let i = 0; i < selectedCustomFieldSets.length; i++) {
                if (selectedCustomFieldSets.includes(customFieldSets[index].fieldSetName)) {
                    const prevSelectedCustomFieldSets = [...selectedCustomFieldSets];
                    prevSelectedCustomFieldSets.splice(i, 1);
                    setSelectedCustomFieldSets(prevSelectedCustomFieldSets)
                }
            }
            const prevCustomFieldSetNames = [...props.customFieldSetNames];
            for (let i = 0; i < prevCustomFieldSetNames.length; i++) {
                if (prevCustomFieldSetNames[i] === customFieldSets[index].fieldSetName) {
                    prevCustomFieldSetNames.splice(i, 1)
                }
            }
            props.setCustomFieldSetNames(prevCustomFieldSetNames)
        }
    }

    const getSelectedCustomFieldSets = async () => {
        if (state.portalId) {
            try {
                const [xmlSelected, rows] = await Promise.all([
                    portalService.getSelectedCustomFieldSetsFromXML(state.portalId).catch(() => []),
                    portalService.getClientPortalCustomFields(state.portalId).catch(() => [])
                ]);
                const rowList = Array.isArray(rows) ? rows : [];
                const fromDb = distinctIntakeFormNamesFromPortalRows(rowList);
                const fromXml = Array.isArray(xmlSelected) ? xmlSelected : [];
                setSelectedCustomFieldSets(fromDb.length > 0 ? fromDb : fromXml);
            } catch (e) {
                console.log('cant get selected custom field sets: ', e)
            }
        }
    }

    const getCustomFields = async () => {
        try {
            const response = await portalService.getClientPortalCustomFields(state.portalId);
            if (response && response.length !== 0) {
                const prevFields = response.map(item => ({
                    value: item.Value,
                    type: item.Type,
                    name: item.Name,
                    perClient: item.PerClient,
                    identifier: item.Identifier,
                    intakeForm: item.IntakeForm ?? null
                }));
                setFields(prevFields);
            } else if (props.afterInit) {
                setFields([{ ...defaultCustomField }]);
            }
        } catch (e) {
            console.log('could not get custom fields, ', e);
        }
    }

    return (
        <>
            {!props.afterInit &&
                <>
                    <h3>Include Intake Templates</h3>
                    <Table hover bordered>
                        <thead>
                            <tr>
                                <th>
                                    Template Name
                                </th>
                                <th>
                                    Include
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {customFieldSets.map((customFieldSet, index) => {
                                return (
                                    <tr key={'custom-field-row-' + index}>
                                        <td
                                            onClick={() => {
                                                setEditFieldSet(customFieldSet)
                                                handleShow()
                                            }}
                                        >
                                            {customFieldSet.fieldSetName}
                                        </td>
                                        <td>
                                            <Form.Check
                                                type='checkbox'
                                                onChange={(e) => handleFieldSetSelected(index, e)}
                                                checked={selectedCustomFieldSets.includes(customFieldSet.fieldSetName)}
                                            />
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </Table>
                </>
            }

            <NewCustomFieldSetModal
                show={show}
                onHide={handleClose}
                editFieldSet={editFieldSet}
                fromSettings={false}
            />

            {props.afterInit ? null : <h3>Add Custom Fields</h3>}
            
            <Table className="table-input" bordered>
                <thead>
                    <tr>
                        <th>Field Name</th>
                        <th>Per Client</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {
                        fields.map((field, index) => {
                            return (
                              <tr key={field.id + "-custom-field-row-" + index}>
                                <td>
                                  <Form.Control
                                    id={index.toString()}
                                    className="standard-form"
                                    onChange={(e) => handleChange(e, index)}
                                    value={field.name}
                                    placeholder="enter a field name.."
                                  />
                                </td>
                                <td>
                                  <Form.Check 
                                    type="checkbox"
                                    id={`perClientCheckbox-${index}`}
                                    onChange={(event) =>
                                      handleSwitchToggle(index, event)
                                    }
                                    checked={field.perClient || false}
                                  />
                                </td>
                                <td>
                                  <Button
                                    variant="link"
                                    hidden={field.fromSet}
                                    onClick={() => handleFieldCleared(index)}
                                  >
                                    <i className="fa-regular fa-trash-can"></i>
                                  </Button>
                                </td>
                              </tr>
                            );
                        })
                    }
                </tbody>
                <tfoot>
                    <tr>
                        <td colSpan={3}>
                            <Button
                                variant='primary'
                                onClick={() => addNewRow()}
                            >
                                + Field
                            </Button>
                        </td>
                    </tr>
                </tfoot>
            </Table>
        </>
    )
}

export default AddCustomFields;
